import { useEffect, useState } from "react";
import axios from "axios";
import { ChevronsUpDown, Radar } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { API, fmtUsd } from "@/lib/api";

export const fmtPrice = (p) => {
  if (p >= 1000) return p.toLocaleString(undefined, { maximumFractionDigits: 2 });
  if (p >= 1) return p.toFixed(4);
  return p.toPrecision(4);
};

export default function CoinPicker({ coin, onCoin }) {
  const [open, setOpen] = useState(false);
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    const load = () => axios.get(`${API}/markets`).then((r) => setMarkets(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const pick = (m) => { onCoin(m.symbol, m); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="all-coins-picker" className="chip flex items-center gap-1.5 !border-[#2E3C54] !text-slate-200">
          All coins <span className="text-slate-500">({markets.length || "…"})</span>
          <ChevronsUpDown className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[340px] border-[#2E3C54] bg-[#0F141C] p-0">
        <Command className="bg-transparent text-slate-200">
          <CommandInput data-testid="coin-search-input" placeholder="Search any coin (e.g. PEPE, ADA)…" className="font-mono text-xs" />
          <CommandList className="thin-scroll max-h-[360px]">
            <CommandEmpty className="py-6 text-center font-mono text-xs text-slate-500">No coin found.</CommandEmpty>
            {markets.map((m) => (
              <CommandItem key={m.pair || m.cg_id} value={`${m.symbol} ${m.name || ""} ${m.cg_id || ""}`} onSelect={() => pick(m)} data-testid={`coin-option-${m.symbol}`}
                className={`flex items-center gap-2 font-mono text-xs data-[selected=true]:!bg-[#1E2738] data-[selected=true]:!text-slate-100 ${coin === m.symbol ? "!bg-[#161D2A]" : ""}`}>
                <span className="w-16 truncate font-semibold text-slate-100">{m.symbol}</span>
                {m.source === "coingecko" && <span className="rounded bg-[#1E2738] px-1 text-[9px] text-slate-400">CG</span>}
                {m.tracked && <Radar className="h-3 w-3 text-[#00E5FF]" />}
                <span className="ml-auto text-slate-300">${fmtPrice(m.price)}</span>
                <span className={`w-14 text-right ${m.change_pct >= 0 ? "text-[#00E676]" : "text-[#FF3D00]"}`}>
                  {m.change_pct >= 0 ? "+" : ""}{m.change_pct}%
                </span>
                <span className="hidden w-14 text-right text-slate-500 sm:inline">{fmtUsd(m.volume_usd)}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
